import React, { useCallback } from "react";
import ErrorBoundary from "components/ErrorBoundary";
import { computeWorldLocationLabel } from "src/utils/worldLocationLabel";
import { AlgoliaCruiseHit } from "types/algolia";
import { CruiseCardModel, TerminalPort } from "components/CruiseCard/types";
import type { CruiseGroupCode } from "@silversea-ssc/web-cruise-groups";
import {
  FycPromosContentFragment,
  FycShipsContentFragment
} from "types/graphql-types";
import { getCurrentVersionAt } from "src/utils/ship";
import { toUTCDate } from "src/utils/date";
import { filterValidPromosContent } from "src/utils/promos";
import {
  Multilingual,
  AlgoliaCruiseEncoded,
  MultilingualHierarchicalFacet
} from "@silversea-ssc/dotcom-api-types/Algolia";
import { FareCode } from "@silversea-ssc/web-fares";
import { isPresent } from "src/utils/checks";
import { Language } from "src/utils/localization/languages/types";
import { useLanguage } from "src/utils/localization/languages/I18nContext";
import EmotionalCruiseCard from "components/CruiseCard/EmotionalCruiseCard/EmotionalCruiseCard";
import { removeDraft } from "src/utils/sanityLocation";
import { BackgroundColor } from "@silversea-ssc/ui-components-react";
import type { SendEventForHits } from "instantsearch.js/es/lib/utils";
import { trackAlgoliaResultClick } from "components/Algolia/algoliaAnalytics";
import { useGeolocation } from "src/utils/localization/geolocation/GeolocationContext";
import { match, P } from "ts-pattern";
import { Currencies } from "@silversea-ssc/dotcom-read-model-types/Currency";
import { AvailableWebPrice } from "@silversea-ssc/dotcom-api-types/Prices";
import { CountryCode } from "@silversea-ssc/dotcom-read-model-types/Common";
import { useRollbarInstance } from "src/gatsby/plugins/gatsby-plugin-rollbar/RollbarProvider";
import { hierarchicalFacetValue } from "components/FindYourCruise/Components/utils";
import type { SearchResults } from "algoliasearch-helper";
import { SanityLanguageCode } from "@silversea-ssc/dotcom-api-types/SanityContent";

export type CruiseCardResultProps = {
  hit: AlgoliaCruiseHit;
  refinements: SearchResults.Refinement[] | undefined;
  filters: string | undefined;
  showDestination?: boolean;
  promosBadgeContent: FycPromosContentFragment[];
  shipsContent: FycShipsContentFragment[];
  backgroundColor: BackgroundColor;
  onClick?: (
    e: React.MouseEvent<HTMLAnchorElement>,
    cruiseCard: CruiseCardModel,
    hit: AlgoliaCruiseHit
  ) => void;
  algoliaSendEvent: SendEventForHits;
};

type CruiseCardProps = React.ComponentProps<typeof EmotionalCruiseCard>;

const CruiseCardResult: React.FC<
  React.PropsWithChildren<CruiseCardResultProps>
> = ({
  hit,
  refinements,
  filters,
  promosBadgeContent,
  shipsContent,
  backgroundColor,
  onClick,
  algoliaSendEvent,
  ...otherProps
}) => {
  const language = useLanguage();
  const geolocation = useGeolocation();
  const rollbarInstance = useRollbarInstance();
  const { showDestination = false } = otherProps;

  const {
    objectID: cruiseCode,
    available,
    days,
    content,
    departurePort,
    arrivalPort,
    destinationNames,
    destinationWebCodes,
    cruiseType,
    cruiseGroup,
    comboType,
    cruiseTagsTree,
    countries
  } = hit;

  const countrySpecificInfo = extractCountrySpecificInfo(
    countries,
    geolocation?.countryCode,
    rollbarInstance
  );

  const {
    p: shortPrice,
    c: currency,
    pc: promoCodes,
    f: shortFareCode
  } = countrySpecificInfo;
  const fareCode = toFareCode(shortFareCode);
  const leadPriceFrom = toAvailableWebPrice(fareCode, shortPrice);

  const { shipId, image, url } = content || {};

  const departureUTCDate = toUTCDate(departurePort.itineraryDate);
  const arrivalUTCDate = toUTCDate(arrivalPort.itineraryDate);

  const shipContent = shipsContent.find(
    (s) => removeDraft(s._id) == shipId
  ) || {
    versions: []
  };
  const shipVersion = getCurrentVersionAt(
    shipContent.versions,
    departureUTCDate
  );
  const shipName = shipContent.name || "";
  const shipPassengerCount = shipVersion?.passengerCount;
  const shipCrewCount = shipVersion?.crewCount;

  const relativeDetailUrl =
    localizedContent(language, url)
      ?.replace("https://www.silversea.com", "")
      ?.replace("/en", "") || "#";
  const relativeDetaulUrlWithAlgoliaTracking = hit.__queryID
    ? `${relativeDetailUrl}?queryID=${hit.__queryID}`
    : relativeDetailUrl;

  const activePromosBadge = promoContentForAlgoliaCode(
    promosBadgeContent,
    promoCodes || []
  );

  const promoBadges = filterValidPromosContent(
    activePromosBadge.map((p) => ({
      code: p.configuration?.code,
      title: p.title?.localized
    }))
  );

  const _onClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, cruiseCard: CruiseCardModel) => {
      trackAlgoliaResultClick(algoliaSendEvent, hit, "Cruise card clicked");
      onClick?.(e, cruiseCard, hit);
    },
    [algoliaSendEvent, hit, onClick]
  );
  const cruiseCardProps: CruiseCardProps = {
    voyageCode: cruiseCode,
    image,
    currency: toCurrencyCode(currency),
    leadPriceFrom: leadPriceFrom,
    departureDate: departureUTCDate,
    arrivalDate: arrivalUTCDate,
    shipName,
    passengerCount: shipPassengerCount || 0,
    crewCount: shipCrewCount || 0,
    durationInDays: days || 0,
    //starText: localizedContent(language, starText),
    departurePort: algoliaCruisePortToTerminalPort(language, departurePort),
    arrivalPort: algoliaCruisePortToTerminalPort(language, arrivalPort),
    available,
    detailUrl: relativeDetaulUrlWithAlgoliaTracking,
    destinationWebCode: destinationWebCodes[0],
    destinationName: firstLocalizedContent(language, destinationNames),
    isExpedition: cruiseType == "Expedition",
    showDestination,
    cruiseGroup: cruiseGroup as CruiseGroupCode,
    promosBadge: promoBadges,
    cruiseTags:
      cruiseTagsFromTree(cruiseTagsTree, refinements, filters, language) ?? [],
    comboType,
    backgroundColor
  };

  return <EmotionalCruiseCard {...cruiseCardProps} onClick={_onClick} />;
};

const CruiseCardResultsWithErrorBoundary: React.FC<
  React.PropsWithChildren<CruiseCardResultProps>
> = (props) => {
  return (
    <ErrorBoundary>
      <CruiseCardResult {...props} />
    </ErrorBoundary>
  );
};

export default React.memo(CruiseCardResultsWithErrorBoundary);

const promoContentForAlgoliaCode = (
  content: FycPromosContentFragment[],
  promoCodes: readonly number[]
): FycPromosContentFragment[] => {
  return content.filter(
    (c) =>
      c.showInCruiseCard &&
      c.configuration?.algoliaCode &&
      promoCodes.includes(c.configuration.algoliaCode)
  );
};

const cruiseTagsFromTree = (
  cruiseTagsTree: MultilingualHierarchicalFacet,
  refinements: SearchResults.Refinement[] | undefined,
  filters: string | undefined,
  language: Language
): string[] | undefined => {
  const key = language.sanityCode;
  const refinementsCategories = cruiseTagsTreeRefinements(refinements);
  refinementsCategories.push(...extractCruiseTagsFromFilter(filters, key));
  const languageTree = cruiseTagsTree?.[key] || cruiseTagsTree?.["en"];
  return languageTree?.level1
    .filter(isSelectedTagCategory(refinementsCategories))
    .map(hierarchicalFacetValue);
};

const isSelectedTagCategory =
  (refinementsCategories: string[]) => (tag: string) =>
    refinementsCategories.length == 0 ||
    refinementsCategories
      .map((refinement) => refinement.split(" > ")[0])
      .filter(isPresent)
      .includes(tag.split(" > ")[0]);

const cruiseTagsTreeRefinements = (
  refinements: SearchResults.Refinement[] | undefined
) =>
  refinements
    ?.filter((r) => r.attributeName.startsWith("cruiseTagsTree"))
    .map((r) => r.name) ?? [];

const extractCruiseTagsFromFilter = (
  filters: string | undefined,
  language: SanityLanguageCode
): string[] => {
  if (!filters) return [];

  const regex = new RegExp(
    `cruiseTagsTree\\.${language}\\.level0:"([^"]+)"`,
    "g"
  );
  const matches: string[] = [];
  let match;

  while ((match = regex.exec(filters)) !== null) {
    matches.push(match[1] as string);
  }

  return matches;
};

const algoliaCruisePortToTerminalPort = (
  language: Language,
  port: AlgoliaCruiseHit["arrivalPort"]
): TerminalPort | undefined => {
  const city = localizedContent(language, port.city);
  const region = localizedContent(language, port.region);
  const country = localizedContent(language, port.country);

  return isPresent(city)
    ? {
        name: city,
        portCode: port.cityCode,
        worldLocation: computeWorldLocationLabel(region, country)
      }
    : undefined;
};

const localizedContent = <T extends readonly string[] | string>(
  language: Language,
  content: Multilingual<T> | undefined
): T | undefined => {
  const key = language.sanityCode;
  return content?.[key] || content?.["en"];
};

const firstLocalizedContent = (
  language: Language,
  content: Multilingual<readonly string[]> | undefined
) => localizedContent(language, content)?.[0];

const toAvailableWebPrice = (
  fareCode: FareCode,
  price: AlgoliaCruiseEncoded["countries"][number]["p"] | undefined
): AvailableWebPrice | undefined =>
  match(price)
    .with(P.nullish, () => undefined)
    .with({ t: "w" }, (p) => ({
      type: "waitlist" as const,
      webFareCode: fareCode,
      configuredAmount: p.a
    }))
    .with({ t: "p" }, (p) => ({
      type: "price" as const,
      webFareCode: fareCode,
      amount: p.a
    }))
    .with({ t: "d" }, (p) => ({
      type: "discounted" as const,
      webFareCode: fareCode,
      amount: p.a,
      originalAmount: p.oa,
      promoConfigurationCodes: []
    }))
    .exhaustive();

const toCurrencyCode = (
  short: AlgoliaCruiseEncoded["countries"][number]["c"] | undefined
) => Currencies.find((currency) => currency[0] === short) ?? "USD";

// this is a copy of the fare code mapping in Algolia Schema definition in @silversea-ssc/dotcom-api-types
const ShortFareCodeMapping = {
  DoorToDoorV2: "D2" as const,
  Essential: "E" as const,
  PortToPortV2: "P2" as const,
  Plus: "PL" as const,
  Basic: "BS" as const,
  LastMinute: "LM" as const
};

const toFareCode = (
  short: AlgoliaCruiseEncoded["countries"][number]["f"] | undefined
) =>
  Object.keys(ShortFareCodeMapping).find(
    (key) => ShortFareCodeMapping[key] === short
  ) as FareCode;

const extractCountrySpecificInfo = (
  countries: AlgoliaCruiseEncoded["countries"] | undefined,
  countryCode: CountryCode | undefined,
  rollbarInstance: ReturnType<typeof useRollbarInstance>
) => {
  const undefinedResult = {
    p: undefined,
    c: undefined,
    pc: [],
    f: undefined
  };
  try {
    const countryInfo = countryCode ? countries?.[countryCode] : undefined;

    return countryInfo || undefinedResult;
  } catch (e) {
    console.error("Error extracting country specific info", e);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (rollbarInstance) rollbarInstance.error(e as any);
    return undefinedResult;
  }
};
